import type { FC } from 'react';
import { Suspense } from 'react';
import { useIntl } from 'react-intl';

import type { Company } from '@xing-com/crate-common-graphql-types';
import { spaceXL } from '@xing-com/tokens';

import { TopCompanyCard } from '../../../../components/top-company-card/top-company-card';
import { useTopCompanies } from '../../../../hooks/use-top-companies/use-top-companies';
import { createTopCompanies } from '../../../../models/top-companies';

import * as Styled from './top-companies-section.styles';

const LIMIT = 10;

const CustomHeader = () => {
  const { formatMessage } = useIntl();
  return (
    <div>
      <Styled.Title size="small" noMargin>
        {formatMessage({ id: 'CP_TOP_COMPANIES_SECTION_TITLE' })}
      </Styled.Title>
      <Styled.Subtitle sizeConfined="large">
        {formatMessage({ id: 'CP_TOP_COMPANIES_SECTION_SUBTITLE' })}
      </Styled.Subtitle>
    </div>
  );
};

const TopCompaniesSectionInner: FC = () => {
  const { data } = useTopCompanies({
    limit: LIMIT,
    offset: 0,
  });

  const collection = data?.topCompanies?.collection ?? [];

  const companies = collection
    .map((item) => item?.company)
    .filter((company) => company) as Company[];

  const topCompanies = createTopCompanies(companies);

  return (
    <Styled.CarouselSection
      variant="custom"
      headerElement={<CustomHeader />}
      useFullWidth
      itemWidthConfined="368px"
      itemWidthWide="368px"
      paddingBottom={spaceXL}
      paddingTop={spaceXL}
    >
      {topCompanies?.map((company, i) => (
        <TopCompanyCard key={`company-${i}`} item={company} />
      ))}
    </Styled.CarouselSection>
  );
};

const LoadingFallback = () => {
  return (
    <Styled.CarouselSection
      variant="custom"
      headerElement={<CustomHeader />}
      useFullWidth
      itemWidthConfined="368px"
      itemWidthWide="368px"
      paddingBottom={spaceXL}
      paddingTop={spaceXL}
    >
      {Array.from({ length: LIMIT }, (_, index) => (
        <TopCompanyCard.Skeleton key={`skeleton-${index}`} />
      ))}
    </Styled.CarouselSection>
  );
};

export const TopCompaniesSection: FC = () => {
  return (
    <div data-testid="top-companies-section">
      <Suspense fallback={<LoadingFallback />}>
        <TopCompaniesSectionInner />
      </Suspense>
    </div>
  );
};
